import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector, useDispatch } from "react-redux";
import { Card, Dropdown } from "react-bootstrap";
import { getInvoiceAnalytics } from "../../../redux/reducers/invoiceSlice";

export default function DashboardInvoiceChart() {
  const dispatch = useDispatch();
  const { analytics } = useSelector((state) => state.invoice);
  const activeBusinessId = useSelector((state) => state.business.activeBusinessId);
  const [range, setRange] = useState("week");

  useEffect(() => {
    if (activeBusinessId) {
      dispatch(getInvoiceAnalytics({ businessId: activeBusinessId, range }));
    }
  }, [activeBusinessId, range, dispatch]);

  const graphData = analytics?.graphData || [];
  const totalInvoiced  = graphData.reduce((acc, curr) => acc + (curr.invoicedAmount    || 0), 0);
  const totalCollected = graphData.reduce((acc, curr) => acc + (curr.collectedAmount   || 0), 0);
  const totalOutstanding = graphData.reduce((acc, curr) => acc + (curr.outstandingAmount || 0), 0);

  const collectedPct   = totalInvoiced > 0 ? ((totalCollected   / totalInvoiced) * 100).toFixed(1) : 0;
  const outstandingPct = totalInvoiced > 0 ? ((totalOutstanding / totalInvoiced) * 100).toFixed(1) : 0;

  const chartOptions = {
    chart: { type: "donut", height: 240 },
    labels: ["Collected", "Outstanding"],
    colors: ["#28B0C4", "#e2e8f0"],
    plotOptions: {
      pie: {
        donut: {
          size: "76%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Collected",
              formatter: () => `$${totalCollected.toLocaleString()}`,
              style: { fontSize: "12px", fontWeight: 700, color: "#28B0C4" },
            },
            value: {
              show: true,
              fontSize: "20px",
              fontWeight: 800,
              color: "#1e293b",
              offsetY: 5,
              formatter: (val) => `$${parseFloat(val).toLocaleString()}`,
            },
          },
        },
      },
    },
    stroke: { show: true, width: 3, colors: ["#fff"] },
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: {
      enabled: true,
      theme: "light",
      y: { formatter: (val) => `$${val.toLocaleString()}` },
    },
  };

  const rows = [
    {
      label: "Invoiced",
      value: totalInvoiced,
      dot: "#28B0C4",
      badge: { text: "Total", bg: "#d1fae5", color: "#10b981" },
    },
    {
      label: "Collected",
      value: totalCollected,
      dot: "#28B0C4",
      badge: { text: `${collectedPct}%`, bg: "#e8f8fb", color: "#28B0C4" },
    },
    {
      label: "Outstanding",
      value: totalOutstanding,
      dot: "#e2e8f0",
      badge: { text: `${outstandingPct}%`, bg: "#fef3c7", color: "#f59e0b" },
    },
  ];

  return (
    <Card className="border-0 shadow-sm rounded-4 h-100 d-flex flex-column" style={{ padding: "18px 20px" }}>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <div className="fw-bold text-dark" style={{ fontSize: "15px" }}>Structure</div>
          <div className="text-muted" style={{ fontSize: "11px" }}>Invoice breakdown</div>
        </div>
        <Dropdown>
          <Dropdown.Toggle
            size="sm"
            className="border-0 rounded-pill fw-bold"
            style={{
              fontSize: "11px",
              color: "#64748b",
              backgroundColor: "#f1f5f9",
              boxShadow: "none",
            }}
            id="chart-range-dropdown"
          >
            {range.charAt(0).toUpperCase() + range.slice(1)}
          </Dropdown.Toggle>
          <Dropdown.Menu className="shadow-sm border-0 rounded-3 mt-1">
            {["week", "month", "year"].map((r) => (
              <Dropdown.Item
                key={r}
                onClick={() => setRange(r)}
                active={range === r}
                className="small fw-semibold"
              >
                {r.charAt(0).toUpperCase() + r.slice(1)}ly
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Donut chart */}
      <div className="text-center">
        <ReactApexChart
          options={chartOptions}
          series={[totalCollected, totalOutstanding]}
          type="donut"
          height={220}
        />
      </div>

      {/* Breakdown rows */}
      <div className="mt-2">
        {rows.map((row, idx) => (
          <div
            key={row.label}
            className={`d-flex justify-content-between align-items-center py-2 ${idx < rows.length - 1 ? "border-bottom" : ""}`}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle"
                style={{ width: 8, height: 8, background: row.dot, flexShrink: 0 }}
              />
              <span className="small fw-semibold text-dark">{row.label}</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="small fw-bold text-dark">${row.value.toLocaleString()}</span>
              <span
                className="rounded-pill px-2 fw-semibold"
                style={{ fontSize: "10px", background: row.badge.bg, color: row.badge.color }}
              >
                {row.badge.text}
              </span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}